import { useEffect, useState } from "react";
import { Users, Barbell, QrCode } from "@phosphor-icons/react";
import api from "../../lib/api";
import CustomMetricsPanel from "../../components/CustomMetricsPanel";

/**
 * TrainerAssessment — performance-test dashboard.
 *
 * Iter 60.2 — split from Metrics so body composition (weight, body-fat, photos)
 * stays cleanly separate from strength/conditioning benchmarks (bike, dead hang,
 * broad jump, 500m row, etc.). Trainer defines the assessment templates here
 * and can log results for any client.
 */
export default function TrainerAssessment() {
  const [clients, setClients] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [scanSummary, setScanSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get("/clients"),
          api.get("/pr-scans/summary").catch(() => ({ data: null })),
        ]);
        setClients(c.data);
        if (c.data.length && !selectedId) setSelectedId(c.data[0].id);
        setScanSummary(s.data);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  const selected = clients.find((c) => c.id === selectedId);

  return (
    <div className="space-y-8" data-testid="trainer-assessment-page">
      <div>
        <div className="tracker text-brand mb-3">[ Performance tests ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">Assessment.</h1>
        <p className="text-zinc-500 mt-2">Bike times, dead hang, broad jump, sprints — define your test protocol once, then log results per client.</p>
      </div>

      {/* Iter 60.7 — QR scan analytics */}
      {scanSummary && scanSummary.total_scans > 0 && (
        <div className="card-flat p-4 sm:p-6" data-testid="pr-scan-summary">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <QrCode size={18} weight="duotone" className="text-brand-light" />
                <h3 className="font-display text-xl">Inbound scans</h3>
              </div>
              <p className="text-xs text-zinc-500">People who scanned a client&apos;s shared PR card and landed on your public page. Last {scanSummary.period_days} days.</p>
            </div>
            <div className="text-right">
              <div className="font-display text-4xl text-emerald-400" data-testid="pr-scan-total">{scanSummary.total_scans}</div>
              <div className="tracker text-[10px] text-zinc-500">TOTAL SCANS</div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="tracker text-[10px] text-zinc-500 mb-2">TOP CLIENTS BY REFERRAL</div>
              {scanSummary.by_client.length === 0 ? (
                <div className="text-[11px] text-zinc-600 italic">No client-attributed scans yet.</div>
              ) : (
                <ol className="space-y-1">
                  {scanSummary.by_client.slice(0, 5).map((r, i) => (
                    <li key={r.client_id} className="flex items-center justify-between px-3 py-1.5 text-xs border border-white/5" data-testid={`pr-scan-client-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="tracker text-[10px] text-zinc-500 w-5">#{i + 1}</span>
                        <span>{r.client_name}</span>
                      </div>
                      <span className="font-mono font-bold text-emerald-400">{r.scans}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div>
              <div className="tracker text-[10px] text-zinc-500 mb-2">TOP TESTS BY REFERRAL</div>
              {scanSummary.by_metric.length === 0 ? (
                <div className="text-[11px] text-zinc-600 italic">No test-attributed scans yet.</div>
              ) : (
                <ol className="space-y-1">
                  {scanSummary.by_metric.slice(0, 5).map((r, i) => (
                    <li key={r.metric_id} className="flex items-center justify-between px-3 py-1.5 text-xs border border-white/5" data-testid={`pr-scan-metric-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="tracker text-[10px] text-zinc-500 w-5">#{i + 1}</span>
                        <span>{r.metric_name}</span>
                      </div>
                      <span className="font-mono font-bold text-emerald-400">{r.scans}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Client picker */}
        <div className="card-flat p-3 h-fit">
          <div className="tracker text-zinc-500 px-2 py-1 flex items-center gap-2">
            <Users size={12} weight="bold" /> Clients ({clients.length})
          </div>
          <div className="mt-2 max-h-[60vh] overflow-y-auto">
            {clients.length === 0 ? (
              <div className="text-zinc-600 text-xs px-2 py-4 italic">No clients yet.</div>
            ) : (
              clients.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  className={`w-full text-left px-3 py-2.5 border-l-2 hover:bg-white/5 ${selectedId === c.id ? "border-brand bg-brand/10" : "border-transparent"}`}
                  data-testid={`assessment-client-${c.id}`}
                >
                  <div className="font-semibold text-sm">{c.name}</div>
                  <div className="font-mono text-[10px] text-zinc-500">{c.email}</div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Panel */}
        <div>
          {selected ? (
            <CustomMetricsPanel clientId={selected.id} canEditTemplates={true} />
          ) : (
            <div className="card-flat p-10 text-center text-zinc-500">
              <Barbell size={32} weight="duotone" className="mx-auto mb-3 text-brand" />
              Pick a client to define and log assessments.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
